// UI locale resolution and catalogs (docs/project.md §5): the user's locale when signed in,
// otherwise the browser language; catalogs are ru/en/tr at start.
import { DEFAULT_UI_LOCALE, UI_LOCALES, type UiLocale } from '@solungo/core/schema';
import { direction } from '@solungo/core/lang';
import ru from './messages/ru.json';
import en from './messages/en.json';
import tr from './messages/tr.json';

export type Messages = typeof ru;
export const MESSAGES: Record<UiLocale, Messages> = { ru, en: en as Messages, tr: tr as Messages };

export function isUiLocale(v: string | null | undefined): v is UiLocale {
  return !!v && (UI_LOCALES as readonly string[]).includes(v);
}

// Best UI locale for a BCP-47 tag ("ru-RU" → "ru"); default when unsupported.
export function pickUiLocale(...candidates: Array<string | null | undefined>): UiLocale {
  for (const c of candidates) {
    if (!c) continue;
    const base = c.toLowerCase().split(/[-_]/)[0]!;
    if (isUiLocale(base)) return base;
  }
  return DEFAULT_UI_LOCALE;
}

export function browserLocale(): UiLocale {
  if (typeof navigator === 'undefined') return DEFAULT_UI_LOCALE;
  return pickUiLocale(...navigator.languages, navigator.language);
}

// The browser language as a language code for the "native language" default ("ru-RU" → "ru")
export function browserLanguage(): string {
  if (typeof navigator === 'undefined') return 'en';
  const tag = navigator.languages[0] ?? navigator.language ?? 'en';
  return tag.toLowerCase().split(/[-_]/)[0] ?? 'en';
}

export function applyDocumentLocale(locale: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
  document.documentElement.dir = direction(locale);
}
